import React, { useEffect, useState } from 'react';
import Modal from '@jbhi-fi/lanyard-ui/components/Modal';
import { CouponModalContent } from './CouponModalContent';
import { Coupon, CouponOffering } from '../types';
import { PerksContainer } from '../PerksContainer';
import { styles } from './CouponModal.css';
import { Confetti } from '@jbhi-fi/lanyard-ui/components/Confetti';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { getDetectedPlatform } from '../Account/getDetectedPlatform';
import { WalletQRModal } from '../Account/JBWallet/WalletQRModal';

type CouponModalCoupon = Omit<Coupon, 'couponReference' | 'allocatedDate' | 'redemptionDate'>;
export type AddToWalletUrls = {
  appleLink?: string;
  googleLink?: string;
};

type CouponModalProps = {
  isOpen: boolean;
  coupon: CouponModalCoupon;
  isLoading: boolean;
  playCouponAppliedAnimation?: boolean;
  couponOffering?: CouponOffering;
  onApplyCoupon: (code: string) => void;
  onRemoveCoupon: (code: string) => void;
  onClose: () => void;
  generateAddToWalletQRCode?: (url: string) => Promise<string | undefined>;
  addToWalletUrls?: AddToWalletUrls;
  addToWalletErrorCode?: number;
  shouldShowAddToWalletButtons?: boolean;
};

const getRedemptionCondition = (coupon: CouponModalCoupon) => {
  if (coupon.canBeRedeemedInStore && !coupon.canBeRedeemedOnline) {
    return 'instore';
  }
  if (coupon.canBeRedeemedOnline && !coupon.canBeRedeemedInStore) {
    return 'online';
  }
  return undefined;
};

export const CouponModal = ({
  isOpen,
  coupon,
  isLoading,
  playCouponAppliedAnimation,
  couponOffering,
  onApplyCoupon,
  onRemoveCoupon,
  onClose,
  generateAddToWalletQRCode,
  addToWalletUrls,
  addToWalletErrorCode,
  shouldShowAddToWalletButtons,
}: CouponModalProps) => {
  const isMobile = useBreakpoint('md', 'down');
  const redemptionCondition = getRedemptionCondition(coupon);
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  const [clickedWallet, setClickedWallet] = useState<'apple' | 'google' | null>(null);
  const platform = getDetectedPlatform();
  const showModal = isOpen && !isQrModalOpen;
  const walletUrl =
    platform === 'ios' || clickedWallet === 'apple' ? addToWalletUrls?.appleLink : addToWalletUrls?.googleLink;

  return (
    <>
      {shouldShowAddToWalletButtons ? (
        <WalletQRModal
          isOpen={isQrModalOpen}
          onClose={() => setIsQrModalOpen(false)}
          walletUrl={walletUrl}
          generateAddToWalletQRCode={generateAddToWalletQRCode}
        />
      ) : null}
      <Modal show={showModal} onClose={onClose} showFrame={true} frame={PerksContainer} customClass={styles.modal}>
        {playCouponAppliedAnimation ? (
          <Confetti
            triggerConfetti={playCouponAppliedAnimation}
            onComplete={onClose}
            origin={isMobile ? 'screenTop' : 'containerTop'}
          />
        ) : null}
        <CouponModalContent
          {...coupon}
          {...coupon.content}
          couponOffering={couponOffering}
          isLoading={isLoading}
          isApplied={coupon.isApplied}
          expiryDate={coupon.content?.expiryDateFormatted}
          termsAndConditions={coupon.content?.termsAndConditions}
          barcode={coupon.content?.barcode}
          redemptionCondition={redemptionCondition}
          onApplyCoupon={onApplyCoupon}
          onRemoveCoupon={onRemoveCoupon}
          setIsQrModalOpen={setIsQrModalOpen}
          setClickedWallet={setClickedWallet}
          addToWalletUrls={addToWalletUrls}
          addToWalletErrorCode={addToWalletErrorCode}
          shouldShowAddToWalletButtons={shouldShowAddToWalletButtons}
        />
      </Modal>
    </>
  );
};
